// resolveKbdPlatform — the boundary reader that decides which platform
// register Kbd shortcut hints render in (ui-design-synthesis §2).
//
// AUDIENCE NOTE (AI register): this is the ONE place the UI derives the
// shortcut platform. Precedence, in order:
//
//   1. The DesktopBridge platform (`window.bildaDesktop.platform`) — the
//      authoritative value, derived once in the Electron main process from
//      `process.platform`. Present iff the app runs inside the desktop shell.
//   2. A minimal navigator sniff for the WEB fallback shell. Only the
//      mac-vs-not distinction matters visually (the "windows" and "linux"
//      registers render identically as text caps), so any non-Apple
//      environment honestly reads as "windows".
//   3. "mac" outside a browser context (tests/SSR) — the pre-platform visual
//      status quo.
//
// PURITY: like getDesktopBridge, this is a boundary reader, not a component
// concern. Impure hosts (AppShellHost and the shell components it mounts)
// call it — typically once — and feed the result to pure components as the
// `platform` prop (Kbd et al.). Pure components must NOT import this module.

import type { KbdPlatform } from "../components/Kbd/Kbd.js";
import { getDesktopBridge } from "./desktopBridge.js";

// True when the browser environment self-describes as Apple. Reads the
// legacy-but-universal `navigator.platform` first, then the UA string —
// both untyped world input, both only trusted for this one cosmetic choice.
const navigatorIsMac = (): boolean => {
    if (typeof navigator === "undefined") return false;
    return /mac|iphone|ipad/i.test(
        `${navigator.platform} ${navigator.userAgent}`,
    );
};

export const resolveKbdPlatform = (): KbdPlatform => {
    const bridge = getDesktopBridge();
    if (bridge !== undefined) return bridge.platform;
    if (typeof navigator === "undefined") return "mac";
    return navigatorIsMac() ? "mac" : "windows";
};
